<template>
    <app-layout title="Products">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Products - Logs</template>

                <template #actions>
                    <jet-tertiary-button type="button" class="h-7 leading-none" @click="$inertia.get(backUrl)">
                        Back
                    </jet-tertiary-button>
                </template>
            </page-header>

            <div class="flex px-8">
                <div class="flex gap-x-2 w-3/4">
                    <multiselect
                        v-model="product"
                        :options="productsOptions"
                        :canDeselect="false"
                        :canClear="false"
                        :searchable="true"
                        class="h-7 multi-sm multi-custom text-base"
                        openDirection="bottom"
                        @change="handleSubmit"
                    />
                    <div class="flex flex-col flex-1">
                        <jet-input type="text" class="h-7" name="search" v-model="form.search" @input="handleSubmit" placeholder="Search..."/>
                    </div>
                    <div class="self-end">
                        <jet-tertiary-button type="button" class="py-1 h-7" @click="handleReset">
                            Reset
                        </jet-tertiary-button>
                    </div>
                </div>
            </div>

            <div class="px-8 mt-2">
                <div class="rounded-t overflow-hidden border border-b-0 w-full">
                    <table class="table w-full">
                        <thead>
                            <tr>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="p.title">Product</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="l.attribute">Attribute</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="l.original">Original Value</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="l.new">New Value</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="l.created_at">Date</sortable-header>
                                <sortable-header class="text-left" @sort="handleSort" :direction="sortDirection" :column="sortColumn" sort-by="u.name">User</sortable-header>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="log in logs.data" :key="log.id">
                                <td v-text="log.name" class="text-left" style="white-space: nowrap;"/>
                                <td v-text="log.attribute" class="text-left"/>
                                <td v-html="log.original" class="text-left" style="word-break: break-word"/>
                                <td v-html="log.new" class="text-left" style="word-break: break-word"/>
                                <td v-text="log.date" class="text-left" style="white-space: nowrap;"/>
                                <td v-text="log.user" class="text-left" style="white-space: nowrap;"/>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="flex justify-between items-center mt-4 w-full">
                    <pagination class="mt-0 mb-0" :links="logs.links" />

                    <div class="w-40">
                        <multiselect
                            v-model.number="form.paginate"
                            :options="paginateOptions"
                            :canDeselect="false"
                            :canClear="false"
                            :classes="{ singleLabel: 'w-full px-2' }"
                            class="h-8 multi-sm multi-custom text-base"
                            openDirection="top"
                            @change="handleSubmit"
                        />
                    </div>
                </div>
            </div>
        </form>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetLabel from '@/Jetstream/Label';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import SettingsNavigation from '@/Pages/Settings/Partials/SettingsNavigation';
import Multiselect from '@vueform/multiselect';
import Pagination from '@/Jetstream/Pagination'
import SortableHeader from '@/Shared/SortableHeader';

import '@vueform/multiselect/themes/default.css';

export default {
    setup(props) {
        const form = useForm({
            paginate: props.paginate,
            search: props.search,
            sortColumn: props.sortColumn,
            sortDirection: props.sortDirection,
        });

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        JetLabel,
        JetInput,
        JetInputError,
        JetDialogModal,
        JetConfirmationModal,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        SettingsNavigation,
        Multiselect,
        Pagination,
        SortableHeader,
    },

    props: ['logs', 'products', 'paginate', 'search', 'sortColumn', 'sortDirection'],

    data() {
        return {
            product: +route().params.product || 'all',
            productsOptions: [
                { value: 'all', label: 'All' },
                ...this.products
            ],
            paginateOptions: [
                { value: 10, label: 'Show 10' },
                { value: 20, label: 'Show 20' },
                { value: 50, label: 'Show 50' },
                { value: 100, label: 'Show 100' },
            ],
        }
    },

    computed: {
        backUrl () {
            const params = route().params;

            if (params.return) {
                return params.return;
            }

            return route('products.index', {
                page: this.$page.props.page.products,
            });
        },
    },

    methods: {
        handleSubmit: _.debounce(function () {
            this.form.get(route('products.logs', this.product), { preserveState: true });
        }, 250),

        handleReset () {
            this.product = 'all';
            this.form.search = '';
            this.form.sortColumn = 'l.created_at';
            this.form.sortDirection = 'desc';
            this.handleSubmit();
        },

        handleSort(sort) {
            this.form.sortColumn = sort.column;
            this.form.sortDirection = sort.direction;
            this.handleSubmit();
        },
    },
}
</script>

<style scoped>

</style>
